import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/** Animates a number toward `target` over `duration` ms (design.md: 200ms). Instant with reduced motion. */
export function useTick(target: number, enabled = true, duration = 200): number {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(target);
  const current = useRef(target);

  useEffect(() => {
    if (!enabled || reduced) {
      current.current = target;
      setValue(target);
      return;
    }
    const from = current.current;
    if (from === target) return;
    let raf = 0;
    // Start from the first frame's timestamp: it shares a clock with later frames
    // (performance.now() can sit on a different clock, e.g. in jsdom).
    let start: number | null = null;
    const step = (now: number) => {
      start ??= now;
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - p) * (1 - p);
      const v = p === 1 ? target : from + (target - from) * eased;
      current.current = v;
      setValue(v);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, enabled, reduced, duration]);

  return value;
}
